import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { Monitor, Zap, Activity, ShieldCheck, MapPin } from 'lucide-react';
import { PageHero, Section, SectionTitle } from '@/components/shared/Section';
import { fadeUp } from '@/lib/motion';

export function OperacionEnVivoPage() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });
  
  return (
    <div>
      <PageHero
        badge="MONITOREO 24/7"
        badgeIcon={Activity}
        h1="Operación en tiempo real"
        subtitle="Monitoreo continuo de nuestra red de 1,900 dispositivos distribuidos en 9 ciudades de Chile. Datos actualizándose en este momento."
      />

      {/* Stats Grid */}
      <Section>
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-200 mb-8 mx-auto">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Actualizado Ahora</span>
        </div>

        <div ref={ref} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {[
            { value: 1900, label: 'Dispositivos online', suffix: '', color: '#0EA5E9', icon: Monitor },
            { value: 23587, label: 'Transacciones hoy', suffix: '', color: '#0191B4', icon: Zap },
            { value: 98.7, label: 'Uptime este mes', suffix: '%', color: '#3B82F6', icon: Activity },
            { value: 57, label: 'Alertas activas', suffix: '', color: '#60A5FA', icon: ShieldCheck },
          ].map((stat, i) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm text-center"
              >
                <Icon className="w-8 h-8 mx-auto mb-3" style={{color: stat.color}} strokeWidth={1.5} />
                <div className="text-3xl font-bold mb-1" style={{fontFamily:'Syne,sans-serif', color: stat.color}}>
                  {inView && <CountUp end={stat.value} duration={2} decimals={stat.suffix === '%' ? 1 : 0} />}
                  {stat.suffix}
                </div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">{stat.label}</div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      {/* Chile Map Simple */}
      <Section alt>
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 p-8">
            <div className="flex items-center justify-center gap-2 mb-6">
              <MapPin className="w-5 h-5 text-[#0191B4]" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold text-[#0A2540]" style={{fontFamily:'Syne,sans-serif'}}>
                Presencia Nacional
              </h3>
            </div>
            
            <div className="relative max-w-md mx-auto">
              <div className="relative bg-white rounded-2xl p-8 border border-slate-200 shadow-sm">
                <div className="text-center space-y-4">
                  {[
                    { city: 'Santiago', devices: 850, color: '#10B981' },
                    { city: 'Valparaíso-Viña', devices: 220, color: '#10B981' },
                    { city: 'Concepción', devices: 180, color: '#10B981' },
                    { city: 'Temuco', devices: 140, color: '#10B981' },
                    { city: 'Antofagasta', devices: 150, color: '#10B981' },
                    { city: 'Puerto Montt', devices: 120, color: '#10B981' },
                    { city: 'Arica', devices: 110, color: '#10B981' },
                    { city: 'Punta Arenas', devices: 80, color: '#10B981' },
                    { city: 'Pucón', devices: 50, color: '#10B981' },
                  ].map((location, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full animate-pulse" style={{backgroundColor: location.color}} />
                        <span className="text-sm font-medium text-slate-700">{location.city}</span>
                      </div>
                      <div className="text-sm font-bold" style={{color: location.color}}>
                        {location.devices} online
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Update indicator */}
            <div className="text-center mt-6">
              <div className="inline-flex items-center gap-2 text-xs text-slate-400">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Actualizando cada 5 segundos
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
